import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./WaypointMap.css";
import CustomSelect from "../shared/CustomSelect";

const DEFAULT_CENTER = [47.05, 15.42]; // fallback (Graz) when the drone has no stored location
const DEFAULT_ZOOM = 14;

// ── Tile layer definitions ──────────────────────────────────────────

/** Standard streets / traffic view (default). */
const streetLayer = L.tileLayer(
  "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  {
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
);

/** Terrain / topographic view — shows elevation contours, hillshading, forests. */
const terrainLayer = L.tileLayer(
  "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
  {
    attribution:
      '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> contributors',
    maxZoom: 17,
  },
);

/** Satellite imagery view. */
const satelliteLayer = L.tileLayer(
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  {
    attribution:
      "&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
    maxZoom: 19,
  },
);

/** Named map of base layers for the layer switcher. */
const baseLayers = {
  Streets: streetLayer,
  Terrain: terrainLayer,
  Satellite: satelliteLayer,
};

/** Options for the custom layer-select dropdown. */
const LAYER_OPTIONS = [
  { value: "Streets", label: "Streets" },
  { value: "Terrain", label: "Terrain" },
  { value: "Satellite", label: "Satellite" },
];

function pinIcon(number, isFirst) {
  return L.divIcon({
    className: "waypoint-pin-icon",
    html: `<span class="waypoint-pin ${isFirst ? "waypoint-pin-start" : ""}">${number}</span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function WaypointMap({ points, onAdd, center }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const tileLayerRef = useRef(null); // tracks the active base tile layer
  const onAddRef = useRef(onAdd);
  onAddRef.current = onAdd;
  const [activeLayer, setActiveLayer] = useState("Streets");

  // Mount the map once (no base tiles added here — done by the activeLayer effect).
  useEffect(() => {
    const map = L.map(mapEl.current, {
      center: center || DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    map.on("click", (e) => onAddRef.current({ lat: e.latlng.lat, lng: e.latlng.lng }));
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Switch base tile layer when the user picks a different map style.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const newLayer = baseLayers[activeLayer];
    if (tileLayerRef.current) map.removeLayer(tileLayerRef.current);
    newLayer.addTo(map);
    tileLayerRef.current = newLayer;
  }, [activeLayer]);

  // Recenter when the drone's stored location arrives after mount.
  useEffect(() => {
    if (mapRef.current && center) mapRef.current.setView(center, mapRef.current.getZoom());
  }, [center]);

  // Redraw pins + connecting line whenever points change.
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const latlngs = points.map((p) => [p.lat, p.lng]);
    if (latlngs.length > 1) {
      L.polyline(latlngs, { color: "#0082F8", weight: 3, opacity: 0.85 }).addTo(layer);
    }
    points.forEach((p, i) => {
      L.marker([p.lat, p.lng], { icon: pinIcon(i + 1, i === 0) }).addTo(layer);
    });
  }, [points]);

  return (
    <div className="waypoint-map-wrapper">
      <div ref={mapEl} className="waypoint-map" />
      <div className="map-layer-selector">
        <CustomSelect
          className="map-layer-select"
          value={activeLayer}
          onChange={setActiveLayer}
          options={LAYER_OPTIONS}
        />
      </div>
    </div>
  );
}

export default WaypointMap;
